import { useCallback, useEffect, useMemo, useState } from 'react';

import { useCity } from '../features/city/useCity';
import { hydrate, type CityFile, type CityFileJson } from './cityFile';
import type { Opportunity } from './opportunity';

/**
 * The current city's file, fetched once per page load and held for every
 * component that asks. Under the app's base (`import.meta.env.BASE_URL`), so it
 * works from a subpath and from a deep link like `/e/:id`, where a URL relative
 * to the page would land under `/e/`.
 */

const loads = new Map<string, Promise<CityFile>>();

function load(city: string, fresh = false): Promise<CityFile> {
  let pending = fresh ? undefined : loads.get(city);
  if (!pending) {
    pending = fetch(`${import.meta.env.BASE_URL}data/${city}.json`, fresh ? { cache: 'reload' } : undefined)
      .then((res) => {
        if (!res.ok) throw new Error(`The ${city} listings answered ${res.status}`);
        return res.json() as Promise<CityFileJson>;
      })
      .then(hydrate);
    // A failure is not cached: the next ask tries again.
    pending.catch(() => loads.delete(city));
    loads.set(city, pending);
  }
  return pending;
}

export interface CityData {
  file: CityFile | null;
  /** Every opportunity in the file, in the file's order. */
  all: Opportunity[];
  byId: ReadonlyMap<string, Opportunity>;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

const NONE: Opportunity[] = [];

export function useCityData(): CityData {
  const { city } = useCity();
  const [state, setState] = useState<{ city: string; file: CityFile | null; error: string | null }>({
    city,
    file: null,
    error: null,
  });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    load(city, attempt > 0).then(
      (file) => live && setState({ city, file, error: null }),
      (err: unknown) => live && setState({ city, file: null, error: err instanceof Error ? err.message : 'Failed to load' }),
    );
    return () => {
      live = false;
    };
  }, [city, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  // A city switch shows nothing of the old city while the new file loads.
  const file = state.city === city ? state.file : null;
  const error = state.city === city ? state.error : null;
  const all = file?.opportunities ?? NONE;
  const byId = useMemo(() => new Map(all.map((o) => [o.id, o])), [all]);

  return { file, all, byId, loading: !file && !error, error, reload };
}
